import React from 'react';
import { Dialog } from '@mui/material';

type SvgProps = { size?: 'md' | 'sm'; strokeWidth?: number };

function GmSvg({ size = 'md', strokeWidth = 2, children }: React.PropsWithChildren<SvgProps>) {
  const px = size === 'sm' ? 14 : 18;
  return (
    <svg
      className={size === 'sm' ? 'gm-icon gm-icon-sm' : 'gm-icon'}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function IconNote() {
  return (
    <GmSvg>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </GmSvg>
  );
}

export function IconTodo() {
  return (
    <GmSvg>
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </GmSvg>
  );
}

export function IconClose() {
  return (
    <GmSvg size="sm">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </GmSvg>
  );
}

export function IconSearch() {
  return (
    <GmSvg size="sm">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </GmSvg>
  );
}

export function IconPlus() {
  return (
    <GmSvg size="sm">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </GmSvg>
  );
}

export function IconPencil() {
  return (
    <GmSvg size="sm">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </GmSvg>
  );
}

export function IconTrash({ size = 'sm' }: { size?: 'md' | 'sm' }) {
  return (
    <GmSvg size={size}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6" />
    </GmSvg>
  );
}

export function IconPerson() {
  return (
    <GmSvg size="sm">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </GmSvg>
  );
}

export function IconCap() {
  return (
    <GmSvg size="sm">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </GmSvg>
  );
}

export function IconLock() {
  return (
    <GmSvg size="sm">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </GmSvg>
  );
}

export function IconUsers() {
  return (
    <GmSvg size="sm">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </GmSvg>
  );
}

export function IconCheck() {
  return (
    <GmSvg size="sm" strokeWidth={3}>
      <polyline points="20 6 9 17 4 12" />
    </GmSvg>
  );
}

export function IconChat() {
  return (
    <GmSvg>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </GmSvg>
  );
}

export function IconBell() {
  return (
    <GmSvg>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </GmSvg>
  );
}

export function IconFlag() {
  return (
    <GmSvg>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </GmSvg>
  );
}

export function IconSend() {
  return (
    <GmSvg size="sm">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </GmSvg>
  );
}

export function formatModalDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

export function GmDeleteDialog({
  open,
  title,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} PaperProps={{ className: 'gm-modal gm-modal-confirm' }}>
      <div className="gm-modal-head">
        <div className="gm-modal-head-icon danger">
          <IconTrash size="md" />
        </div>
        <div className="gm-modal-head-text">
          <div className="gm-modal-title">{title}</div>
          <div className="gm-modal-subtitle">This can&apos;t be undone.</div>
        </div>
      </div>
      <div className="gm-modal-actions">
        <button type="button" className="gm-btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="gm-btn-primary danger" onClick={onConfirm}>
          <IconTrash />
          Delete
        </button>
      </div>
    </Dialog>
  );
}
